import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { FlashList } from '@shopify/flash-list';
import { useAppSelector, useAppStore } from 'store/hooks';
import { AppView, Indicator, rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useFetchOld, { muteAll } from 'hooks/useFetch';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import EmptyState from 'features/invest/components/EmptyState';
import { selectUserId } from 'reducers/selectors';
import createStyleSheets from 'utils/createStyleSheets';
import { addFlashListSectionedDataToActivities, getActivitiesSectionedFlashlist } from 'utils/formatting/activities';
import { isWeb } from '../../../constants';
import { getPotActivities } from '../actions';
import PotsActivityRow from '../components/PotsActivityRow';
import { selectInitialActivitiesLoadingForPot } from '../reducers/selectors';
const strings = {
    emptyStateSubtitle: 'Add money to your pot to see something here.',
};
const keyExtractor = (item) => String(item.id);
const AnimatedFlashList = Animated.createAnimatedComponent(FlashList);
const PotsActivityScreen = withAnimatedNavigationHeader(({ route, scrollHandler }) => {
    const potId = route.params.pot.id;
    const store = useAppStore();
    const myId = useAppSelector(selectUserId);
    const [, , , fetch] = useFetchOld(muteAll);
    const potOwnerUserId = route.params.pot.userId || myId;
    const hideSpaceUserAvatar = route.params?.hideSpaceUserAvatar;
    const shouldLoadSecondPage = useRef(false);
    const pot = route.params?.pot;
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    const loading = useAppSelector((state) => state.unpersistedPotsReducer.requestingPotActivities?.[potId]);
    const activities = useAppSelector((state) => state.unpersistedPotsReducer.potActivities[potId]);
    const potPendingActivities = useAppSelector((state) => state.unpersistedPotsReducer.potPendingActivities?.[potId]);
    const initialLoading = useAppSelector((state) => selectInitialActivitiesLoadingForPot(state, potId));
    const flashListData = useMemo(() => {
        const activitiesFlashList = getActivitiesSectionedFlashlist(activities, 'timestamp');
        if (potPendingActivities?.length) {
            return [
                {
                    id: 'Pending',
                    sectionTitle: 'Pending',
                },
                ...addFlashListSectionedDataToActivities(potPendingActivities),
                ...activitiesFlashList,
            ];
        }
        return activitiesFlashList;
    }, [activities, potPendingActivities]);
    useEffect(() => {
        if (!initialLoading) {
            fetch(getPotActivities(potId, { status: 'COMPLETED', paging: { page: 1, perPage: 30 }, potOwnerUserId }));
        }
        // Runs only once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const renderItem = useCallback(({ item }) => <PotsActivityRow hideSpaceUserAvatar={hideSpaceUserAvatar} pot={pot} item={item}/>, [pot, hideSpaceUserAvatar]);
    const loadMoreData = useCallback(() => {
        const token = store.getState().unpersistedPotsReducer.potActivitiesPaginationToken?.[potId];
        if (token) {
            fetch(getPotActivities(potId, {
                paging: {
                    token,
                },
                potOwnerUserId,
            }));
        }
    }, [potId, potOwnerUserId]);
    useEffect(() => {
        if (!loading && shouldLoadSecondPage.current) {
            shouldLoadSecondPage.current = false;
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const onLoadNextPage = useCallback(() => {
        if (loading) {
            shouldLoadSecondPage.current = true;
        }
        else {
            loadMoreData();
        }
    }, [loadMoreData, loading]);
    const content = useMemo(() => {
        if (initialLoading && !flashListData.length) {
            return <Indicator dark style={styles.indicatorStyle}/>;
        }
        if (flashListData.length === 0) {
            return (<View style={paddingHorizontalStyle}>
            <EmptyState activity subtitle={strings.emptyStateSubtitle}/>
          </View>);
        }
        return (<AnimatedFlashList data={flashListData} renderItem={renderItem} onScroll={scrollHandler} scrollEventThrottle={16} keyExtractor={keyExtractor} onEndReachedThreshold={0.1} onEndReached={onLoadNextPage} contentContainerStyle={contentContainerStyle} showsVerticalScrollIndicator scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}/>);
    }, [
        contentContainerStyle,
        flashListData,
        initialLoading,
        onLoadNextPage,
        paddingHorizontalStyle,
        renderItem,
        scrollHandler,
        styles.indicatorStyle,
    ]);
    return <AppView withFrame={false}>{content}</AppView>;
}, ({ colors }) => ({
    title: 'Activity',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets((colors) => ({
    indicatorStyle: {
        backgroundColor: 'transparent',
    },
    scrollView: {
        flex: 1,
    },
    sectionTitle: {
        paddingTop: rem(24),
        paddingBottom: rem(16),
        marginHorizontal: rem(16),
        backgroundColor: colors.background.light,
    },
}));
export default PotsActivityScreen;
